/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import PropTypes from 'prop-types';

import { isBrowser } from 'react-device-detect';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
} from '../../../../utilities/sharedQueries';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { WalletScreens } from '../../constants';
import { currencies, exchange } from '../../../../utilities/constants';
import CurrencyIcon from '../../../../components/CurrencyIcon';
import Badge from '../../../../components/Badge';
import FocusableInputArea from '../../partials/FocusableInputArea';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { useLedgerFilteredCurrencies } from '../../../../utilities/sharedHooks/ledgerWallets';

import {
  formatDecimalNumber,
  formatNumber,
  formatNumberWithComma,
} from '../../../../utilities/formatHelpers';

import { WalletsTypes } from '../../../Card/constants';
import { validateNumbers } from '../../../../utilities/formValidation';

const FiatWalletWithdrawal = ({ openScreen, options }) => {
  const { t: translate } = useTranslation();
  const wallet = options ? options.wallet : null;
  const { data: userPreferences } = useQuery(USER_PREFERENCES);
  const displayCurrency = userPreferences.displayCurrency || 'EUR';
  const rate = useRate(wallet.coin, displayCurrency);
  const [fiatAmount, setFiatAmount] = useState(
    formatNumberWithComma(formatNumber(options?.fiatAmount, exchange.FIAT_VALUE)) || 0
  );
  const [fiatAmountDecimal, setFiatAmountDecimal] = useState(options?.fiatAmount || 0);

  const [fiatInputWidth, setFiatInputWidth] = useState(0);

  const [accountDisplayCurrencyValue, setAccountDisplayCurrencyValue] = useState(
    options?.fiatAmount * rate || 0
  );

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const balance = wallet.available_balance * currencies[wallet.coin].conversion;

  const isLoading = false;

  const renderTitle = () => (
    <div className="d-flex flex-row justify-content-between align-items-center w-100">
      <b className="text-f5 text-dark me-auto">
        <CurrencyIcon currencyCode={wallet?.coin} className="me-2" />
        {translate('withdraw_fiat_label').replace('%s', wallet.coin.toUpperCase())}
      </b>
      <p className="text-shadow text-f6">
        {`${translate('available_balance_label')}: `}{' '}
        <span className="text-dark">{`${formatNumber(
          balance,
          exchange.FIAT_VALUE
        )} ${wallet?.coin?.toUpperCase()}`}</span>
      </p>
    </div>
  );

  const [minAmount] = useSetting(
    'server_configurations',
    `${wallet?.coin}_min_fiat_withdrawal_amount`
  );

  const fiatInputSpan = useRef();

  const allowBankWithdrawal = useMemo(() => {
    if (supportedCurrencies && supportedCurrencies[wallet?.coin] && minAmount) {
      return !!(supportedCurrencies[wallet?.coin]?.bank_transfer_fee && minAmount);
    }
    return false;
  }, [supportedCurrencies, minAmount, wallet]);

  const PREDEFINED_AMOUNTS = useMemo(() => {
    if (allowBankWithdrawal) {
      return Array.from({ length: 3 }, (e, i) => i + 1).map((item) => item * minAmount);
    }

    return [50, 100, 200, 500];
  }, [minAmount, allowBankWithdrawal]);

  const isDisabled = useMemo(() => {
    if (allowBankWithdrawal) {
      return fiatAmountDecimal <= 0 || fiatAmountDecimal > balance || fiatAmountDecimal < minAmount;
    }
    return fiatAmountDecimal <= 0 || fiatAmountDecimal > balance;
  }, [minAmount, allowBankWithdrawal, fiatAmountDecimal, balance]);

  const handleChangeValue = (e) => {
    const { value, min } = e.target;

    const { decimalAmount, formattedAmount } = formatDecimalNumber(value.toString(), min);

    if (!validateNumbers(decimalAmount, WalletsTypes.FIAT)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setFiatAmount(0);
      setFiatAmountDecimal(0);
      setAccountDisplayCurrencyValue(0);
      return;
    }

    setFiatAmount(formattedAmount);
    setFiatAmountDecimal(decimalAmount);
    setAccountDisplayCurrencyValue(decimalAmount * rate);
  };

  const renderFooter = () => (
    <button
      type="button"
      loading={isLoading ? 'true' : 'false'}
      disabled={isDisabled}
      className="btn btn-primary py-3"
      onClick={() => {
        if (!allowBankWithdrawal) {
          openScreen(WalletScreens.FIAT_WITHDRAWAL_DETAILS, { ...options, fiatAmount });
        } else {
          openScreen(WalletScreens.FIAT_WITHDRAWAL_REVIEW, {
            ...options,
            fiatAmount: fiatAmountDecimal,
          });
        }
      }}>
      {translate('confirm_continue_btn_title')}
    </button>
  );

  useEffect(() => {
    if (fiatInputSpan.current) {
      const charactersLength =
        fiatInputSpan.current.value.length > 1 ? fiatInputSpan.current.value.length : 1;
      // to avoid having empty input, fix length to 1 if text length is less then 1
      setFiatInputWidth(charactersLength);
    }
  }, [fiatAmount]);

  return (
    <ModalBodyLayout withBack={false} renderTitle={renderTitle} renderFooter={renderFooter}>
      <div>
        <div
          className="text-center"
          style={isBrowser ? { marginTop: '50%' } : { marginTop: '50%', marginBottom: '50%' }}>
          <div className="d-flex flex-row w-100">
            <p className="text-shadow text-f6">{translate('deposit_wallet_enter_amount')}</p>
          </div>
          <div className="mt-2 pt-1">
            <span className="text-shadow">
              {formatNumberWithComma(
                formatNumber(accountDisplayCurrencyValue, exchange.FIAT_VALUE)
              )}{' '}
              {displayCurrency}
            </span>
          </div>
          <FocusableInputArea>
            <input
              min="0"
              type="text"
              className="form-control bg-transparent p-0 text-center border-0 text-f3"
              value={fiatAmount}
              onChange={(e) => handleChangeValue(e)}
              onFocus={(e) => e.target.select()}
              style={{
                width: `${fiatInputWidth + 1}ch`,
              }}
              ref={fiatInputSpan}
            />
            <span className="text-f3">{wallet.coin.toUpperCase()}</span>
          </FocusableInputArea>
          <div className="flex-row-center mt-3 d-flex">
            {PREDEFINED_AMOUNTS.map((amount) => (
              <Badge
                key={amount}
                content={`${amount} ${wallet.coin.toUpperCase()}`}
                onClick={() => {
                  setFiatAmount(formatNumberWithComma(formatNumber(amount, exchange.FIAT_VALUE)));
                  setFiatAmountDecimal(amount);
                  setAccountDisplayCurrencyValue(amount * rate);
                }}
                className={`py-1 px-3 rounded-4 text-white text-f5 cursor-pointer ${
                  fiatAmountDecimal === amount ? 'active' : ''
                }`}
              />
            ))}
            {allowBankWithdrawal && (
              <Badge
                content={translate('max_button_label')}
                onClick={() => {
                  const amount = parseFloat(formatNumber(balance, exchange.FIAT_VALUE));
                  setFiatAmount(formatNumberWithComma(formatNumber(amount, exchange.FIAT_VALUE)));
                  setFiatAmountDecimal(amount);
                  setAccountDisplayCurrencyValue(amount * rate);
                }}
                className={`py-1 px-4 rounded-4 text-white text-f5 cursor-pointer ${
                  fiatAmountDecimal === balance ? 'active' : ''
                }`}
              />
            )}
          </div>
          {allowBankWithdrawal && (
            <div className="alert alert-info mb-4 mt-4">
              <i className="icon icon-info-icon" />
              <span className="content">
                {translate('fiat_withdrawal_amount_note').replace(
                  '__MIN__',
                  `${minAmount} ${wallet?.coin?.toUpperCase()}`
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </ModalBodyLayout>
  );
};

export default FiatWalletWithdrawal;

FiatWalletWithdrawal.propTypes = {
  openScreen: PropTypes.func.isRequired,
  options: PropTypes.object,
};
FiatWalletWithdrawal.defaultProps = {
  options: null,
};
